import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, errorMessage } from '@/lib/api';
import { parseLocalDateTime, splitDateTime, toLocalInput } from '@/lib/dates';
import { openDialog } from '@/lib/overlays';
import { toast, toastError } from '@/lib/toast';
import { Callout, Segmented, Spinner } from '@/ui/bits';
import { DateTimeField } from '@/ui/DateTime';
import { DialogBody, DialogCancel, DialogFoot, DialogHead } from '@/ui/Dialog';
import { InputField, SelectField, TextAreaField } from '@/ui/Field';
import { Icon } from '@/ui/Icon';
import { CALL_OUTCOMES, firstName, type CallOutcome, type NoteItem, type RecordSheet } from '@shared/record';
import { keys, prependTimeline } from './queries';
import { markFresh } from './Timeline';
import { isSubmitKey } from './util';

// "Log call": when it happened, which way, how long, how it went and what you talked about, optionally on a deal.

function LogCallForm({ sheet, close }: { sheet: RecordSheet; close: () => void }) {
  const qc = useQueryClient();
  const people = sheet.type === 'company' ? sheet.people : [];
  const [personId, setPersonId] = useState(sheet.type === 'person' ? sheet.id : (people[0]?.id ?? ''));
  const [when, setWhen] = useState(() => toLocalInput(new Date()));
  const [direction, setDirection] = useState<'out' | 'in'>('out');
  const [minutes, setMinutes] = useState('15');
  const [outcome, setOutcome] = useState<CallOutcome>('connected');
  const deals = sheet.openDeals.filter((d) => sheet.type === 'company' || d.personId === sheet.id || d.companyId === sheet.company?.id);
  const [dealId, setDealId] = useState(deals.length === 1 ? deals[0]!.id : '');
  const [body, setBody] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (busy) return;
    const local: Record<string, string> = {};
    const { date, time } = splitDateTime(when);
    const at = parseLocalDateTime(date, time);
    if (!at) local.occurredAt = 'Pick a date and time';
    // The server allows a few minutes of clock difference, so the check here matches it.
    else if (at.getTime() > Date.now() + 5 * 60_000) local.occurredAt = 'Pick a time that has already passed';
    const mins = minutes.trim() === '' ? null : Number(minutes);
    if (mins !== null && (!Number.isInteger(mins) || mins < 0)) local.callDurationMin = 'Use whole minutes';
    if (!at || Object.keys(local).length) {
      setErrors(local);
      return;
    }
    setBusy(true);
    setErrors({});
    setFormError(null);
    try {
      const saved = await api.post<NoteItem>('/notes', {
        kind: 'call',
        body: body.trim(),
        personId: personId || null,
        companyId: sheet.type === 'company' ? sheet.id : null,
        dealId: dealId || null,
        occurredAt: at.toISOString(),
        callOutcome: outcome,
        callDurationMin: mins,
        callDirection: direction,
      });
      prependTimeline(qc, sheet.type, sheet.id, { kind: 'note', id: `note:${saved.id}`, at: saved.occurredAt, note: saved });
      markFresh(`note:${saved.id}`);
      void qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) });
      toast('Call logged', {
        icon: 'check-circle',
        undo: () => {
          api
            .post('/bin', { type: 'note', ids: [saved.id] })
            .then(() => qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) }))
            .catch((err) => toastError(err));
        },
      });
      close();
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length) setErrors(err.fields);
      else setFormError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const who = sheet.type === 'person' ? sheet.name : sheet.name;
  return (
    <div
      onKeyDown={(e) => {
        if (isSubmitKey(e)) {
          e.preventDefault();
          void save();
        }
      }}
    >
      <DialogHead title="Log call" sub={`A call with ${who}, kept on the timeline for the team.`} glyph="phone" onClose={close} />
      <DialogBody>
        <div className="rec-form">
          {sheet.type === 'company' ? (
            <SelectField
              label="With"
              className="span2"
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
              error={errors.personId}
              options={[
                { value: '', label: `${sheet.name}, nobody in particular` },
                ...people.map((p) => ({ value: p.id, label: p.title ? `${p.name} · ${p.title}` : p.name })),
              ]}
            />
          ) : null}
          <DateTimeField
            label="When"
            value={when}
            max={toLocalInput(new Date())}
            onChange={setWhen}
            error={errors.occurredAt}
            data-autofocus
          />
          <div className="field">
            <div className="field-top">
              <span className="flabel">Direction</span>
            </div>
            <Segmented
              label="Direction"
              value={direction}
              onChange={setDirection}
              options={[
                { id: 'out', label: 'You called', icon: 'arrow-up-right' },
                { id: 'in', label: `${sheet.type === 'person' ? firstName(sheet.name) : 'They'} called`, icon: 'phone' },
              ]}
            />
          </div>
          <InputField
            label="Duration"
            type="number"
            min={0}
            max={1440}
            inputMode="numeric"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            error={errors.callDurationMin}
            aside="minutes"
          />
          <SelectField
            label="Outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as CallOutcome)}
            error={errors.callOutcome}
            options={CALL_OUTCOMES.map((o) => ({ value: o.id, label: o.label }))}
          />
          {deals.length ? (
            <SelectField
              label="Deal"
              className="span2"
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
              error={errors.dealId}
              help="The deal's last activity moves to this call."
              options={[{ value: '', label: 'No deal' }, ...deals.map((d) => ({ value: d.id, label: d.name }))]}
            />
          ) : null}
          <TextAreaField
            label="What you talked about"
            className="span2"
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            error={errors.body}
            placeholder="Decisions, numbers, next steps…"
          />
        </div>
        {formError ? (
          <Callout tone="danger" role="alert">
            {formError}
          </Callout>
        ) : null}
      </DialogBody>
      <DialogFoot>
        <span className="hint">Ctrl or ⌘ and Enter to save</span>
        <span className="spacer" />
        <DialogCancel onClick={close} />
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>
          {busy ? <Spinner label="Saving" /> : <Icon name="phone" />}
          Log call
        </button>
      </DialogFoot>
    </div>
  );
}

export function openLogCall(sheet: RecordSheet): void {
  openDialog({ label: 'Log call', render: (close) => <LogCallForm sheet={sheet} close={close} /> });
}
